import type { TEntity } from "store/beneficiaryStore";
import type { CustomFieldValue, ICustomField } from "store/customFieldsStore";

export const CUSTOM_FIELD_UUID_MAX_LENGTH = 128;
export const CUSTOM_FIELD_UUID_PATTERN = /^[A-Za-z0-9_-]+$/;

export type CustomFieldResource = ICustomField["relatedResource"];

export type CustomFieldWritePayload = {
    name: string;
    helperText: string;
    valueType: CustomFieldValue;
    formLocation: string;
    entityType: TEntity;
    isRequired: boolean;
    showOnCreation: boolean;
    showOnPortal: boolean;
    includeInSalesExport: boolean;
    relatedResource?: CustomFieldResource;
    uuid?: string;
};

export const flagToBoolean = (value: unknown) => value === true || value === 1 || value === "1";

/** Empty identifier is generated by the API. Otherwise it must match the backend pattern. */
export const validateCustomFieldIdentifier = (uuid: string) => {
    const trimmed = uuid.trim();
    if (!trimmed) return null;
    if (trimmed.length > CUSTOM_FIELD_UUID_MAX_LENGTH) return "CustomFields.FormModal.IdentifierTooLong";
    if (!CUSTOM_FIELD_UUID_PATTERN.test(trimmed)) return "CustomFields.FormModal.IdentifierInvalid";
    return null;
};

export const termFieldsForMode = (fields: ICustomField[], mode: "new" | "edit") =>
    fields.filter((field) => field.relatedResource === "term" && (mode === "edit" || !!field.showOnCreation));

type FormFlags = {
    name: string;
    helperText: string;
    valueType: CustomFieldValue;
    entityType: TEntity;
    isRequired: unknown;
    showOnCreation: unknown;
    showOnPortal?: unknown;
    includeInSalesExport?: unknown;
    uuid?: string;
};

export const buildCustomFieldPayload = ({
    data,
    resource,
    formLocation,
    mode,
}: {
    data: FormFlags;
    resource: CustomFieldResource;
    formLocation: string;
    mode: "new" | "edit";
}): CustomFieldWritePayload => {
    const isTerm = resource === "term";
    const payload: CustomFieldWritePayload = {
        name: data.name,
        helperText: data.helperText,
        valueType: data.valueType,
        formLocation,
        entityType: data.entityType,
        isRequired: flagToBoolean(data.isRequired),
        showOnCreation: flagToBoolean(data.showOnCreation),
        showOnPortal: isTerm ? false : flagToBoolean(data.showOnPortal),
        includeInSalesExport: isTerm || resource !== "title" ? false : flagToBoolean(data.includeInSalesExport),
    };

    if (mode === "new") {
        payload.relatedResource = resource;
        const identifier = data.uuid?.trim();
        if (identifier) payload.uuid = identifier;
    }

    return payload;
};
